// PHASE_4 / B1-frontend — Görevler (admin/manager task inbox).
// Faz 8.6 / Batch A7 restyles the VISUAL layer to the novem tasks language
// (NovemHR-ekranlar tasks.jsx TaskList port: serif PageHeader+em, the KPI
// StatCard strip, a novem SearchInput + a state FilterPillBar replacing the raw
// <select>, the Card-wrapped table). The DATA layer is untouched — the SAME
// keyset list via GET /v1/tasks with a status filter.
//
// The server scopes the rows by the caller's tier (manager_subtree /
// executive_broad see the broader set; the subtree narrow is a P5/ADR-017
// carryover, so for B1 the higher tiers see the tenant set). The assignee column
// is shown here (an admin acts ACROSS people) — but the lifecycle actions stay
// assignee-only SERVER-side: a complete on a task you don't own 403s, surfaced
// honestly by the shared TasksTable. No money.
//
// Pure, shell-agnostic Content (ADR-014 §2): the SAME building blocks as the
// employee Görevlerim twin, role-swapped under the SINGLE parameterized
// AppShell at /tasks.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardList, Clock3, CircleCheckBig, Inbox } from "lucide-react";

import {
  Button,
  FilterPillBar,
  PageHeader,
  ScopeGate,
  SearchInput,
  StatCard,
  type FilterPillItem,
} from "@novemai/components";

import { useKeysetQuery } from "@/api/keyset";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { formatCountWithMore } from "@/lib/count";
import { fetchTasksPage, type TaskRow } from "./api";
import { AssignTaskModal } from "./assign-task-modal";
import { taskDisplayTitle } from "./task-display-title";
import { TASK_CAP_CREATE, taskCapabilities } from "./tasks-capabilities";
import { TasksTable } from "./tasks-table";
import { useDebouncedSearchParam, useFilterParam } from "@/lib/use-url-filters";

const STATUS_OPTIONS: readonly string[] = [
  "open",
  "in_progress",
  "snoozed",
  "completed",
  "dismissed",
  "expired",
];


export interface TasksContentProps {
  /**
   * Convenience capability set for ScopeGate (server is the authority). When
   * omitted it is derived from the caller's effective permissions. The read
   * list itself is tier-scoped server-side so it needs no client gate.
   */
  readonly allowedCapabilities?: readonly string[];
}

export function TasksContent({ allowedCapabilities }: TasksContentProps = {}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { capabilities: allowedCells } = useEffectivePermissions();
  const capabilities = allowedCapabilities ?? [...taskCapabilities(allowedCells)];
  // The status filter is server-side (they ride the query key), so losing them on
  // Back also threw away the fetched page — SEM-6350.
  const [statusFilter, setStatusFilter] = useFilterParam("status");
  // The box used to send `kind`, an exact machine token nobody types, so every
  // human search came back empty. The list route has no title search, so the
  // box narrows the LOADED window by display title and says so while more
  // pages remain.
  const [titleInput, setTitleInput] = useDebouncedSearchParam("q");
  const [assignOpen, setAssignOpen] = useState(false);

  const filters = statusFilter !== "" ? { status: statusFilter } : {};

  const query = useKeysetQuery<TaskRow>({
    // Filters in the key so a filter change refetches a fresh first page.
    queryKey: ["tasks", "admin", statusFilter],
    fetchPage: (cursor) => fetchTasksPage(cursor, filters),
  });

  const needle = titleInput.trim().toLocaleLowerCase(i18n.language);
  const shownQuery =
    needle === ""
      ? query
      : {
          ...query,
          items: query.items.filter((row) =>
            taskDisplayTitle(row, t).toLocaleLowerCase(i18n.language).includes(needle),
          ),
        };

  // KPI strip — honest counts over the loaded keyset window (server scopes by
  // tier; "+" while more pages remain).
  const openCount = query.items.filter((r) => r.status === "open").length;
  const inProgressCount = query.items.filter((r) => r.status === "in_progress").length;
  const completedCount = query.items.filter((r) => r.status === "completed").length;

  function clearFilters(): void {
    setStatusFilter("");
    setTitleInput("");
  }

  // Status filter rendered as a novem pill bar (TaskList "Durum" control).
  const statusFilterItems: ReadonlyArray<FilterPillItem> = [
    { value: "", label: t("tasks.filter_all") },
    ...STATUS_OPTIONS.map((s) => ({ value: s, label: t(`tasks.status.${s}`) })),
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={
          <>
            {t("tasks.title_lead")} <em>{t("tasks.title_em")}</em>
          </>
        }
        description={t("tasks.description")}
        actions={
          <ScopeGate required={TASK_CAP_CREATE} allowed={capabilities}>
            <Button variant="primary" size="sm" onClick={(): void => setAssignOpen(true)}>
              {t("tasks.assign_cta")}
            </Button>
          </ScopeGate>
        }
      />

      <AssignTaskModal
        open={assignOpen}
        onClose={(): void => setAssignOpen(false)}
        onAssigned={query.refetch}
      />

      {/* KPI strip — mockup StatCard satırı (TaskList). */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("tasks.kpi_total")}
          value={formatCountWithMore(query.items.length, query.hasNextPage)}
          tone="accent"
          icon={<ClipboardList size={18} />}
        />
        <StatCard
          label={t("tasks.kpi_open")}
          value={formatCountWithMore(openCount, query.hasNextPage)}
          tone="info"
          icon={<Inbox size={18} />}
        />
        <StatCard
          label={t("tasks.kpi_in_progress")}
          value={formatCountWithMore(inProgressCount, query.hasNextPage)}
          tone="warning"
          icon={<Clock3 size={18} />}
        />
        <StatCard
          label={t("tasks.kpi_completed")}
          value={formatCountWithMore(completedCount, query.hasNextPage)}
          tone="success"
          icon={<CircleCheckBig size={18} />}
        />
      </div>

      <div className="space-y-3 rounded-[14px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="min-w-[220px] flex-1">
            <SearchInput
              aria-label={t("tasks.filter_title")}
              placeholder={t("tasks.filter_title_placeholder")}
              icon={<ClipboardList size={14} />}
              value={titleInput}
              onChange={(e): void => setTitleInput(e.target.value)}
            />
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t("tasks.filter_clear")}
          </Button>
        </div>
        <FilterPillBar
          aria-label={t("tasks.filter_status")}
          items={statusFilterItems}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {needle !== "" && query.hasNextPage && (
        <p className="text-[12.5px] text-[var(--fg-muted)]">
          {t("tasks.filter_title_partial", { count: query.items.length })}
        </p>
      )}

      <TasksTable
        query={shownQuery}
        caption={t("tasks.list_caption")}
        emptyTitle={t("tasks.empty_title")}
        emptyDescription={t("tasks.empty_description")}
        showAssignee
      />
    </div>
  );
}
